/**
 * Definitions des cinq biomes.
 *
 * Les couleurs sont volontairement saturees: le rendu les melange ensuite par
 * du bruit et par les poids de biome, ce qui les adoucit beaucoup.
 * Les densites de props sont par hectare (10 000 m2). Le monde fait 25 ha.
 */
import type { BiomeDef, BiomeId } from '../types/world.ts'

export const BIOMES: Record<BiomeId, BiomeDef> = {
  plage: {
    id: 'plage',
    nom: 'Plage',
    couleurSol: 0xe8cf86,
    couleurSolVariante: 0xf8eec6,
    sol: 'sable',
    couleurBrume: 0xffe6bd,
    vitesse: 0.85,
    props: { coquillage: 220, herbe: 200, caillou: 32, fleur: 20 },
  },

  foret: {
    id: 'foret',
    nom: 'Forêt tropicale',
    couleurSol: 0x4fae44,
    couleurSolVariante: 0x1c6a35,
    sol: 'herbe',
    couleurBrume: 0xb8e6b4,
    vitesse: 1,
    props: { herbe: 1400, fleur: 200, champignon: 62, caillou: 30 },
  },

  montagne: {
    id: 'montagne',
    nom: 'Montagne',
    couleurSol: 0x9a92b6,
    couleurSolVariante: 0x5f8052,
    sol: 'roche',
    couleurBrume: 0xd4d0ea,
    vitesse: 0.95,
    props: { herbe: 350, caillou: 112, fleur: 60 },
  },

  glacier: {
    id: 'glacier',
    nom: 'Glacier',
    couleurSol: 0xeaf5ff,
    couleurSolVariante: 0x8ad9ea,
    sol: 'neige',
    couleurBrume: 0xdcefff,
    vitesse: 0.8,
    props: { glacon: 120, caillou: 28 },
  },

  desert: {
    id: 'desert',
    nom: 'Désert',
    couleurSol: 0xe2a95a,
    couleurSolVariante: 0xc0653b,
    sol: 'sable',
    couleurBrume: 0xffd69f,
    vitesse: 0.85,
    // Le desert touche maintenant la mer au sud: ses dunes s'y aplatissent en
    // plage, et une plage porte des coquillages. `genererProps` ne les garde que
    // sous 3 m d'altitude, donc ils restent sur l'estran et ne remontent pas
    // dans les dunes.
    props: { caillou: 56, herbe: 90, fleur: 22, coquillage: 60 },
  },

  /*
   * LES TROIS BIOMES DE L'ILE 2.
   *
   * Elle porte ONZE regions et n'a apporte que trois identifiants: le reste de
   * son caractere vit dans `REGIONS` (shared/data/ile2-regions.ts), qui donne a
   * chaque region sa couleur, ses plantes, sa vitesse et sa brume sans rien
   * couter aux tables exhaustives d'ici. Ces trois-la sont montes jusqu'ici
   * parce qu'ils changent quelque chose qu'une region ne sait pas dire.
   */

  /** Le canyon: une matiere sous le pied que rien d'autre n'a. */
  cristal: {
    id: 'cristal',
    nom: 'Canyon de cristal',
    couleurSol: 0x8f9bd6,
    couleurSolVariante: 0xd7c4ee,
    sol: 'cristal',
    couleurBrume: 0xd8dcff,
    vitesse: 0.92,
    props: { caillou: 130, herbe: 180, fleur: 30 },
  },

  /** La jungle: sa faune et son vacarme ne sont pas ceux de la foret. */
  jungle: {
    id: 'jungle',
    nom: 'Jungle',
    couleurSol: 0x3f8f39,
    couleurSolVariante: 0x8fc23c,
    sol: 'terre',
    couleurBrume: 0xc8ecb0,
    vitesse: 0.95,
    props: { herbe: 1500, fleur: 300, champignon: 90, caillou: 26, palmier: 40 },
  },

  /** La ferme: le seul endroit du monde ou des betes domestiques ont un sens. */
  ferme: {
    id: 'ferme',
    nom: 'Ferme',
    couleurSol: 0x8fae4c,
    couleurSolVariante: 0xc7b56a,
    sol: 'terre',
    couleurBrume: 0xe6efc0,
    vitesse: 1,
    props: { herbe: 1200, fleur: 260, caillou: 18 },
  },
}
